import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import { getCompanyLogo } from 'lib/api/company';
import { getDocumentReport } from 'lib/api/document';
import { expressGetCompanyLogo } from 'lib/api/express';
import { getDocumentConciliationRateHistory } from 'lib/api/reports';
import { generateDocumentReportPDF } from 'pages/Finance/Components/Financing/common/exportDocumentReport';
import { FieldVisibilityConfig } from 'pages/Finance/Components/Financing/common/exportDocumentReport/types';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';

import CollectionsSection from './components/CollectionsSection';
import ExportConfigDialog from './components/ExportConfigDialog';
import GeneralSection from './components/GeneralSection';
import StateSection from './components/StateSection';
import SummarySection from './components/SummarySection';
import { DocumentConciliationRateHistory } from './components/types';

type DocumentProfileProps = {
  documentId: string | undefined;
  open: boolean;
  handleClose: () => void;
};

const DocumentProfile = ({
  documentId,
  open,
  handleClose,
}: DocumentProfileProps) => {
  if (!documentId) return;

  const dispatch = useDispatch();
  const { data: document, isPending } = getDocumentReport(dispatch, documentId);

  const user = useSelector((state: RootState) => state.auth.user);
  const [exporting, setExporting] = useState(false);
  const [showExportConfig, setShowExportConfig] = useState(false);
  const [rateHistory, setRateHistory] =
    useState<DocumentConciliationRateHistory | null>(null);

  const handleExportClick = () => {
    setShowExportConfig(true);
  };

  const handleExportConfigClose = () => {
    setShowExportConfig(false);
  };

  const handleExportConfigConfirm = async (
    fieldVisibility: FieldVisibilityConfig,
  ) => {
    if (isPending) return;

    setExporting(true);
    try {
      const imageBase64 = user?.express
        ? await expressGetCompanyLogo(dispatch, user.clientId as string)
        : await getCompanyLogo(dispatch);

      let historyToInclude: DocumentConciliationRateHistory | null =
        rateHistory;
      if (fieldVisibility.rateHistory && !rateHistory) {
        try {
          const loadedHistory = await getDocumentConciliationRateHistory(
            dispatch,
            documentId,
          );
          if (loadedHistory) {
            historyToInclude = loadedHistory;
            setRateHistory(loadedHistory);
          }
        } catch (err) {
          console.error('Error loading rate history for PDF:', err);
        }
      }

      await generateDocumentReportPDF(
        document,
        user?.name || '',
        imageBase64,
        fieldVisibility,
        historyToInclude || undefined,
      );
    } catch (error) {
      console.error('Error exporting PDF:', error);
    } finally {
      setExporting(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <Dialog
        maxWidth="lg"
        fullWidth
        open={open}
        onClose={handleClose}
        scroll="paper"
      >
        <Box
          sx={{
            p: 3,
            position: 'relative',
            maxHeight: '80vh',
            overflow: 'auto',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
              pr: 6,
            }}
          >
            <Typography variant="h3">Detalle documento</Typography>
            <Button
              variant="text"
              onClick={handleExportClick}
              disabled={exporting}
              sx={{
                color: 'primary.main',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                },
              }}
            >
              {exporting ? 'Exportando...' : 'Exportar a PDF'}
            </Button>
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => handleClose()}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
            size="large"
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <GeneralSection documentId={documentId} />
            <Box sx={{ display: 'flex', gap: 3, width: '100%' }}>
              <Box sx={{ flex: 1 }}>
                <SummarySection
                  documentId={documentId}
                  rateHistory={rateHistory}
                  onHistoryLoaded={setRateHistory}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <StateSection documentId={documentId} />
              </Box>
            </Box>
            <CollectionsSection documentId={documentId} />
          </Box>
        </Box>
      </Dialog>

      <ExportConfigDialog
        open={showExportConfig}
        onClose={handleExportConfigClose}
        onConfirm={handleExportConfigConfirm}
      />
    </>
  );
};

export default DocumentProfile;
